import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Lock, Check, Copy, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * UnlockEverythingReel — /share/unlock-everything
 * 3-scene 9:16 screen-recording reel driving anonymous visitors to sign up.
 * Free-while-we-grow angle, no "forever" wording (paid tiers land this week).
 */
const CAPTION = `🏕 300 UK campsites rotate free every Monday on CampCompass — sign up in 60 seconds to unlock all 22,000+.

Real prices · full map · save favourites · Campfire Chat · direct booking.

Paid tiers arrive later this week — early sign-ups keep every feature included, no upgrade needed.

👉 Grab your free account at camp-compass.uk
#CampCompass #UKCamping #FreeMembership`;

export default function UnlockEverythingReel() {
  const copy = () => {
    navigator.clipboard.writeText(CAPTION).then(() => toast.success("Caption copied"));
  };
  return (
    <div className="min-h-screen bg-[#fffbf2]" data-testid="unlock-everything-reel">
      <div className="max-w-[420px] mx-auto p-4 space-y-3">
        <div className="text-center">
          <Link to="/launch-pack" className="text-[11px] mono uppercase tracking-widest text-[#ff4500] hover:underline">← Back to Launch Pack</Link>
          <h1 className="font-display text-2xl mt-1">Reel · Unlock Everything</h1>
          <p className="text-xs text-[#666]">3 scenes · 9:16 · screen-record and post</p>
        </div>
        <button onClick={copy} className="w-full inline-flex items-center justify-center gap-2 bg-[#0e0e0e] text-white text-xs font-black uppercase tracking-widest px-4 py-2.5" data-testid="unlock-copy">
          <Copy className="w-3 h-3" /> Copy caption
        </button>

        {/* Scene 1 */}
        <Scene bg="from-[#0e0e0e] via-[#1a1a1a] to-[#2a1300]" testid="unlock-scene-1">
          <div className="text-white text-center flex-1 flex flex-col items-center justify-center p-6">
            <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 text-[11px] mono uppercase tracking-widest font-black mb-4">
              <Lock className="w-3 h-3" /> Members only
            </div>
            <div className="font-display text-4xl leading-tight mb-3">You&rsquo;re missing<br/>the good stuff.</div>
            <p className="text-white/80 text-sm">Prices · map · favourites · chat.</p>
          </div>
        </Scene>

        {/* Scene 2 */}
        <Scene bg="from-[#ff4500] via-[#e03d00] to-[#a82e00]" testid="unlock-scene-2">
          <div className="text-white text-center flex-1 flex flex-col items-center justify-center p-6">
            <div className="inline-flex items-center gap-1.5 bg-white/20 px-3 py-1 text-[11px] mono uppercase tracking-widest font-black mb-4">
              <Sparkles className="w-3 h-3" /> 60 seconds
            </div>
            <div className="font-display text-4xl leading-tight mb-4">Sign up free.</div>
            <ul className="text-left text-sm space-y-1.5">
              {["All 22,000+ UK campsites (guests: 300 rotate weekly)","Real prices + full map","Save unlimited favourites","Campfire Chat + DMs","Weather + price alerts"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4" strokeWidth={3} /> {t}</li>
              ))}
            </ul>
          </div>
        </Scene>

        {/* Scene 3 */}
        <Scene bg="from-[#0a7f3f] via-[#0e8f4a] to-[#083c1e]" testid="unlock-scene-3">
          <div className="text-white text-center flex-1 flex flex-col items-center justify-center p-6">
            <div className="font-display text-4xl leading-tight mb-3">Free while we grow.</div>
            <p className="text-white/90 text-sm mb-6">Paid tiers arrive later this week.<br/>Early sign-ups keep every feature.</p>
            <div className="bg-white text-[#0a7f3f] font-black uppercase tracking-widest text-xs px-5 py-3 inline-flex items-center gap-2">
              camp-compass.uk <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </Scene>
      </div>
    </div>
  );
}

function Scene({ bg, testid, children }) {
  return (
    <div className={`aspect-[9/16] w-full flex flex-col bg-gradient-to-br ${bg}`} data-testid={testid}>
      {children}
    </div>
  );
}
